import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { supabase } from '../supabase';

const API = 'https://app.recensement-enfantsnonproteges.fr';

const LEXIQUE = [
  { terme: 'AEMO', def: "Assistance Éducative en Milieu Ouvert — mesure de protection de l'enfance." },
  { terme: 'AED', def: "Aide Éducative à Domicile — mesure de soutien à la parentalité." },
  { terme: 'ASE', def: "Aide Sociale à l'Enfance — service départemental de protection de l'enfance." },
  { terme: 'CEDH', def: "Cour Européenne des Droits de l'Homme." },
  { terme: 'IP', def: "Information Préoccupante — signalement adressé à la cellule de recueil des informations préoccupantes." },
  { terme: 'JAF', def: "Juge aux Affaires Familiales — statue sur la garde des enfants." },
  { terme: 'JE', def: "Juge pour Enfants." },
  { terme: 'MIJE', def: "Mesure d'Investigation et d'Évaluation." },
  { terme: 'VSS', def: "Violences Sexuelles et Sexistes." },
  { terme: 'VP/P', def: "Violences Physiques et/ou Psychologiques." },
];

const getEtat = (f) => {
  if (!f.etat_plainte) return 'En cours';
  if (f.etat_plainte.includes('Cassation') || f.etat_plainte.includes('CEDH')) return 'Cassation';
  if (f.etat_plainte.includes('Condamn') || f.etat_plainte.includes('Non-lieu') || f.etat_plainte.includes('Acquittement') || f.etat_plainte.includes('Relaxe')) return 'Jugé';
  return 'En cours';
};

const getTags = (f) => {
  const tags = [];
  if (f.type_violences?.some(v => v.includes('Viol') || v.includes('Agression'))) tags.push({ label: 'VSS', red: false });
  if (f.type_violences?.some(v => v.includes('physique') || v.includes('Physique') || v.includes('coercitif'))) tags.push({ label: 'VP/P', red: false });
  if (f.alerte_urgence) tags.push({ label: 'DANGER IMMÉDIAT', red: true });
  if (f.nb_condamnations_pp > 0) tags.push({ label: 'MAMAN CONDAMNÉE', red: false });
  if (f.enfants?.some(e => e.protege)) tags.push({ label: 'PROTÉGÉ.S', red: false });
  return tags;
};

const getTitre = (f) => {
  const nb = f.enfants?.length || 1;
  const garde = f.garde_agresseur || '';
  if (garde.includes('alternée')) return `Procédure garde alternée ${nb} enfant${nb > 1 ? 's' : ''}.`;
  if (nb > 1) return `Procédure fratrie de ${nb} enfants.`;
  return 'Procédure 1 enfant.';
};

const Logo = () => (
  <div style={{lineHeight:'1.15'}}>
    <div style={{fontSize:'12px',fontWeight:'800',color:'#19232F',textTransform:'uppercase',letterSpacing:'0.04em'}}>Recensement des enfants</div>
    <div style={{fontSize:'20px',fontWeight:'900',color:'#065FD3',textTransform:'uppercase',letterSpacing:'0.02em'}}>Non-Protégés</div>
  </div>
);

export default function Homepage({ onDeposer, onNavigate, onOpenFiche }) {
  const [stats, setStats] = useState({});
  const [fiches, setFiches] = useState([]);
  const [nlEmail, setNlEmail] = useState('');
  const [nlCheck, setNlCheck] = useState(false);
  const [nlSent, setNlSent] = useState(false);
  const [lexOpen, setLexOpen] = useState(false);
  const [sessionUser, setSessionUser] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/stats`).then(r => setStats(r.data)).catch(() => {});
    axios.get(`${API}/api/fiches`).then(r => setFiches(r.data)).catch(() => {});
    supabase.auth.getSession().then(({ data: { session } }) => setSessionUser(session?.user || null));
    supabase.auth.onAuthStateChange((_e, session) => setSessionUser(session?.user || null));
  }, []);

  const goDeposer = () => sessionUser ? onNavigate('compte') : onDeposer();

  const sendNl = async () => {
    if (!nlEmail || !nlCheck) return;
    try { await axios.post(API + '/api/newsletter', { email: nlEmail }); setNlSent(true); } catch(e) { setNlSent(true); }
  };

  const COMPTEURS_1 = [
    { key: 'total_enfants', label: 'enfants recensés' },
    { key: 'alertes_urgentes', label: 'alertes urgentes', red: true },
    { key: 'departements', label: 'départements' },
    { key: 'total_preuves', label: 'preuves déposées' },
    { key: 'total_protectrices', label: 'personnes ayant protégé' },
  ];
  const COMPTEURS_2 = [
    { key: 'total_magistrats', label: 'magistrats intervenus' },
    { key: 'total_agents_ase', label: 'agent ASE' },
    { key: 'total_dossiers_perdus', label: 'dossiers perdus', red: true },
    { key: 'total_jugements', label: 'jugement rendus' },
    { key: 'total_dysfonctionnements', label: 'dysfonctionnements', red: true },
  ];

  return (
    <div style={{fontFamily:'-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',background:'#F1F1F7',minHeight:'100vh'}}>

      {/* HERO */}
      <div style={{background:'#fff',padding:'48px 48px 40px'}}>
        <div style={{fontSize:'11px',fontWeight:'600',color:'#888',textTransform:'uppercase',letterSpacing:'0.1em',marginBottom:'16px'}}>Recensement national</div>
        <h1 style={{fontSize:'clamp(28px,4vw,44px)',fontWeight:'900',color:'#19232F',lineHeight:'1.05',marginBottom:'16px',textTransform:'uppercase'}}>
          LA JUSTICE FRANÇAISE REMET DES ENFANTS<br/>À LEURS <span style={{color:'#6558F4'}}>AGRESSEURS</span>
        </h1>
        <p style={{fontSize:'16px',color:'#444',marginBottom:'8px',lineHeight:'1.5'}}>ou les punit pour les avoir dénoncés en les plaçant à l'ASE.<br/>Nous le documentons.</p>
        <p style={{fontSize:'13px',color:'#777',lineHeight:'1.65',marginBottom:'32px',maxWidth:'560px'}}>Chaque fiche est réelle, datée, vérifiable. Ce recensement documente les défaillances systémiques du système judiciaire français dans la protection des enfants victimes de violences.</p>
        <button onClick={goDeposer} style={{background:'#065FD3',color:'#fff',border:'none',borderRadius:'999px',padding:'12px 28px',fontSize:'13px',fontWeight:'700',cursor:'pointer',textTransform:'uppercase',letterSpacing:'0.04em'}}>RENSEIGNER UNE FICHE</button>
      </div>

      {/* STATISTIQUES */}
      <div style={{background:'#fff',borderTop:'1px solid #eee',padding:'40px 48px'}}>
        <div style={{textAlign:'center',fontSize:'24px',fontWeight:'800',color:'#19232F',marginBottom:'6px'}}>Statistiques</div>
        <div style={{textAlign:'center',fontSize:'13px',color:'#888',marginBottom:'28px'}}>Dernière mise à jour {new Date().toLocaleDateString('fr-FR')}</div>
        {[COMPTEURS_1, COMPTEURS_2].map((row, ri) => (
          <div key={ri} style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:'14px',maxWidth:'860px',margin:'0 auto 14px'}}>
            {row.map(c => (
              <div key={c.key} style={{background:'#fff',border:'1px solid #e4e4ee',borderRadius:'12px',padding:'16px',textAlign:'center'}}>
                <div style={{fontSize:'28px',fontWeight:'800',color:c.red?'#e24b4a':'#6558F4',marginBottom:'6px'}}>{stats[c.key] || 'XX'}</div>
                <div style={{fontSize:'11px',color:'#888',lineHeight:'1.3'}}>{c.label}</div>
              </div>
            ))}
          </div>
        ))}
        <div style={{textAlign:'center',marginTop:'20px'}}>
          <span onClick={() => onNavigate && onNavigate('statistiques')} style={{color:'#065FD3',fontSize:'13px',fontWeight:'600',cursor:'pointer',textDecoration:'underline'}}>Toutes les statistiques →</span>
        </div>
      </div>

      {/* FICHES */}
      <div style={{padding:'40px 48px',background:'#EAEEF8'}}>
        <div style={{textAlign:'center',fontSize:'24px',fontWeight:'800',color:'#19232F',marginBottom:'6px'}}>Fiches</div>
        <div style={{textAlign:'center',fontSize:'13px',color:'#888',marginBottom:'28px'}}>Dernières fiches recensées</div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'14px',maxWidth:'900px',margin:'0 auto'}}>
          {fiches.slice(0,6).map(f => {
            const etat = getEtat(f);
            const tags = getTags(f);
            return (
              <div key={f.id} onClick={() => onOpenFiche && onOpenFiche(f.id)}
                style={{background:'#fff',border:'1px solid #e4e4ee',borderRadius:'12px',padding:'16px',cursor:'pointer'}}
                onMouseEnter={e=>e.currentTarget.style.boxShadow='0 4px 16px rgba(0,0,0,0.08)'}
                onMouseLeave={e=>e.currentTarget.style.boxShadow='none'}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'10px'}}>
                  <span style={{fontSize:'11px',color:'#aaa'}}>{new Date(f.created_at).toLocaleDateString('fr-FR')}</span>
                  <span style={{fontSize:'11px',fontWeight:'600',color:'#555'}}>{etat}</span>
                </div>
                <div style={{display:'flex',gap:'6px',flexWrap:'wrap',marginBottom:'12px'}}>
                  {tags.map((t,i) => <span key={i} style={{fontSize:'10px',padding:'3px 10px',borderRadius:'999px',fontWeight:'700',background:t.red?'#e24b4a':'#065FD3',color:'#fff'}}>{t.label}</span>)}
                </div>
                <div style={{fontSize:'15px',fontWeight:'700',color:'#19232F',lineHeight:'1.3'}}>{getTitre(f)}</div>
                {f.departement && <div style={{fontSize:'11px',color:'#aaa',marginTop:'6px'}}>{f.departement}</div>}
              </div>
            );
          })}
          {fiches.length === 0 && (
            <div style={{gridColumn:'span 3',textAlign:'center',padding:'40px',color:'#aaa',background:'#fff',borderRadius:'12px',border:'1px solid #e4e4ee'}}>
              Aucune fiche publiée pour le moment.
            </div>
          )}
        </div>
        <div style={{textAlign:'center',marginTop:'20px'}}>
          <span onClick={() => onNavigate && onNavigate('fiches')} style={{color:'#065FD3',fontSize:'13px',fontWeight:'600',cursor:'pointer',textDecoration:'underline'}}>Toutes les fiches →</span>
        </div>
      </div>

      {/* ACTUALITÉS */}
      <div style={{padding:'40px 48px',background:'#fff'}}>
        <div style={{textAlign:'center',fontSize:'24px',fontWeight:'800',color:'#19232F',marginBottom:'6px'}}>Actualités</div>
        <div style={{textAlign:'center',fontSize:'13px',color:'#888',marginBottom:'28px'}}>Retrouvez tous les évènements, témoignages et articles de presse RENP</div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'14px',maxWidth:'900px',margin:'0 auto'}}>
          {[
            { badge:'ÉVÉNEMENT', bg:'#065FD3', icon:'📢', date:'10 juillet 2026', temps:'3 min', titre:'Lancement officiel du recensement national des enfants non-protégés' },
            { badge:'TÉMOIGNAGE', bg:'#19232F', icon:'💬', date:'8 juillet 2026', temps:'5 min', titre:'Trois ans de procédure, deux enfants remis au père malgré les preuves' },
            { badge:'PRESSE', bg:'#e4e4ee', color:'#19232F', icon:'📰', date:'7 juillet 2026', temps:'2 min', titre:'France Inter — Interview de Priscilla Majani sur le collectif RENP' },
          ].map((a,i) => (
            <div key={i} style={{background:'#fff',border:'1px solid #e4e4ee',borderRadius:'12px',overflow:'hidden'}}>
              <div style={{padding:'12px 14px 6px',display:'flex',justifyContent:'space-between',fontSize:'11px',color:'#aaa'}}>
                <span>{a.date}</span><span>{a.temps}</span>
              </div>
              <div style={{padding:'0 14px 10px',fontSize:'13px',fontWeight:'600',color:'#19232F',lineHeight:'1.4'}}>{a.titre}</div>
              <div style={{height:'110px',background:'#e8edf5',display:'flex',alignItems:'center',justifyContent:'center',position:'relative',fontSize:'28px'}}>
                {a.icon}
                <span style={{position:'absolute',bottom:'10px',right:'10px',background:a.bg,color:a.color||'#fff',padding:'3px 10px',borderRadius:'4px',fontSize:'10px',fontWeight:'700'}}>{a.badge}</span>
              </div>
            </div>
          ))}
        </div>
        <div style={{textAlign:'center',marginTop:'20px'}}>
          <span style={{color:'#065FD3',fontSize:'13px',fontWeight:'600',cursor:'pointer',textDecoration:'underline'}}>Toutes les actualités →</span>
        </div>
      </div>

      {/* LEXIQUE */}
      <div style={{padding:'20px 48px'}}>
        <div style={{background:'#fff',border:'1px solid #e4e4ee',borderRadius:'12px',overflow:'hidden'}}>
          <div onClick={() => setLexOpen(!lexOpen)} style={{padding:'20px 24px',display:'flex',alignItems:'center',justifyContent:'space-between',cursor:'pointer'}}>
            <div>
              <div style={{fontSize:'18px',fontWeight:'700',color:'#19232F',marginBottom:'4px'}}>Lexique</div>
              <div style={{fontSize:'12px',color:'#888'}}>Abréviations, termes juridiques, retrouvez ici les explications.</div>
            </div>
            <div style={{color:'#aaa',fontSize:'20px'}}>{lexOpen ? '∧' : '∨'}</div>
          </div>
          {lexOpen && (
            <div style={{borderTop:'1px solid #eee',padding:'20px 24px',display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
              {LEXIQUE.map(l => (
                <div key={l.terme} style={{borderRadius:'8px',padding:'12px 14px',background:'#F1F1F7'}}>
                  <div style={{fontSize:'13px',fontWeight:'700',color:'#065FD3',marginBottom:'4px'}}>{l.terme}</div>
                  <div style={{fontSize:'12px',color:'#555',lineHeight:'1.5'}}>{l.def}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* NEWSLETTER */}
      <div style={{background:'#19232F',padding:'40px 48px',display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'32px',alignItems:'start'}}>
        <div>
          <div style={{fontSize:'22px',fontWeight:'800',color:'#fff',marginBottom:'12px'}}>Restez informé</div>
          <div style={{fontSize:'13px',color:'rgba(255,255,255,0.5)',lineHeight:'1.7'}}>Mises à jour des statsiques, nouveaux recensements, nouveautés sur le site...<br/><br/>Inscrivez-vous à notre newsletter pour rester informé !</div>
        </div>
        <div>
          <label style={{color:'rgba(255,255,255,0.7)',fontSize:'12px',display:'block',marginBottom:'8px'}}>Adresse email :</label>
          {nlSent ? (
            <div style={{color:'#4db3ff',fontSize:'14px',fontWeight:'600'}}>✓ Inscription confirmée !</div>
          ) : (
            <>
              <input type="email" value={nlEmail} onChange={e=>setNlEmail(e.target.value)} style={{width:'100%',border:'1px solid rgba(255,255,255,0.2)',borderRadius:'6px',padding:'9px 12px',fontSize:'13px',background:'rgba(255,255,255,0.08)',color:'#fff',marginBottom:'10px'}} />
              <label style={{display:'flex',alignItems:'center',gap:'8px',fontSize:'12px',color:'rgba(255,255,255,0.55)',marginBottom:'14px',cursor:'pointer'}}>
                <input type="checkbox" checked={nlCheck} onChange={e=>setNlCheck(e.target.checked)} />
                Vous acceptez de recevoir nos informations par mail
              </label>
              <button onClick={sendNl} style={{background:'#fff',color:'#19232F',border:'none',borderRadius:'6px',padding:'9px 20px',fontSize:'12px',fontWeight:'700',cursor:'pointer',textTransform:'uppercase',letterSpacing:'0.04em'}}>S'INSCRIRE À LA NEWSLETTER</button>
            </>
          )}
        </div>
        <div style={{fontSize:'12px',color:'rgba(255,255,255,0.35)',lineHeight:'1.7'}}>
          Vous affirmez avoir prit connaissance de notre <a style={{color:'#4db3ff'}}>politique de confidentialité.</a><br/><br/>
          Vous pouvez vous désinscrire à tout moment à l'aide des liens de désinscription ou en nous contactant à l'adresse suivante :<br/>
          <a style={{color:'#4db3ff'}}>recensementenfantsnonproteges@gmail.com</a>
        </div>
      </div>

      {/* FOOTER */}
      <div style={{background:'#0f1a2e',padding:'32px 48px',display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'32px'}}>
        <div>
          <Logo />
          <div style={{fontSize:'12px',color:'rgba(255,255,255,0.35)',lineHeight:'1.65',marginTop:'12px'}}>Collectif de mères protectrices fondé par Priscilla Majani, Maryline Perrinet et Sophie Abida.</div>
        </div>
        <div>
          <ul style={{listStyle:'none'}}>
            {['Politique de confidentialité','RGPD','Cookies','Accessibilité','Sitemap'].map(l => (
              <li key={l} style={{marginBottom:'8px'}}><a style={{color:'rgba(255,255,255,0.45)',fontSize:'13px',textDecoration:'none',cursor:'pointer'}}>{l}</a></li>
            ))}
          </ul>
        </div>
        <div>
          <div style={{color:'#fff',fontSize:'13px',fontWeight:'600',marginBottom:'12px'}}>Suivez-nous</div>
          <div style={{display:'flex',gap:'8px',marginBottom:'20px'}}>
            {['in','X','f','▶'].map(s => (
              <div key={s} style={{width:'32px',height:'32px',borderRadius:'6px',background:'rgba(255,255,255,0.08)',display:'flex',alignItems:'center',justifyContent:'center',color:'rgba(255,255,255,0.5)',fontSize:'12px',fontWeight:'700'}}>{s}</div>
            ))}
          </div>
          <div style={{color:'#fff',fontSize:'13px',fontWeight:'600',marginBottom:'10px'}}>Contacter l'équipe</div>
          <button onClick={() => window.location.href='mailto:recensementenfantsnonproteges@gmail.com'} style={{background:'rgba(255,255,255,0.08)',color:'rgba(255,255,255,0.7)',border:'1px solid rgba(255,255,255,0.15)',borderRadius:'6px',padding:'8px 16px',fontSize:'12px',cursor:'pointer',textTransform:'uppercase',letterSpacing:'0.04em'}}>ENVOYER UN EMAIL</button>
        </div>
      </div>

    </div>
  );
}
